/**
 * macd — Moving Average Convergence Divergence (Appel, 1979).
 *
 * Needed by the Wolf Stack pattern detector (Diaz) — one of the four
 * inputs to Diaz's signature stack alignment check. Could also serve
 * future MACD-divergence patterns.
 *
 * Algorithm (standard):
 *   - MACD line = EMA(close, fast) − EMA(close, slow)            [default 12/26]
 *   - Signal line = EMA(MACD line, signal)                       [default 9]
 *   - Histogram = MACD line − Signal line
 *
 * Returns a parallel array to bars. Warm-up: first `slow + signal − 2`
 * bars have null values (the slow EMA + signal EMA both need to warm up
 * before the histogram is meaningful).
 *
 * Pure function. No side effects. Reuses computeEMA from emas.js so the
 * smoothing behavior matches what the chart shows.
 */

import { computeEMA } from './emas.js';

export const DEFAULT_MACD_FAST = 12;
export const DEFAULT_MACD_SLOW = 26;
export const DEFAULT_MACD_SIGNAL = 9;

/**
 * Compute the MACD series.
 *
 * @param {Array<{time:number, close:number}>} bars  Sorted ascending.
 * @param {Object} [options]
 * @param {number} [options.fast=12]     Fast EMA period.
 * @param {number} [options.slow=26]     Slow EMA period.
 * @param {number} [options.signal=9]    Signal-line smoothing period.
 * @returns {Array<{ time:number|undefined, macd:number|null, signal:number|null, histogram:number|null }>}
 *          Same length as `bars`. Null values during warm-up.
 */
export const computeMACD = (bars, options = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  // Use ?? not || so explicit `fast: 0` falls through to the validation
  // below instead of silently substituting the default.
  const fast = Math.floor(Number(options.fast ?? DEFAULT_MACD_FAST));
  const slow = Math.floor(Number(options.slow ?? DEFAULT_MACD_SLOW));
  const signal = Math.floor(Number(options.signal ?? DEFAULT_MACD_SIGNAL));
  if (![fast, slow, signal].every(n => Number.isFinite(n) && n >= 1)) return [];
  if (fast >= slow) return [];  // sanity: fast must be < slow

  const n = bars.length;
  const fastSeries = computeEMA(bars, fast);
  const slowSeries = computeEMA(bars, slow);

  // Step 1: MACD line = fast − slow, parallel to bars.
  // computeEMA returns { time, value } where value is null during warm-up.
  // For signal-line EMA, we feed a synthetic "bars" of { time, close: macd }
  // shape so we can reuse computeEMA's warm-up logic.
  const macdAsBars = new Array(n);
  for (let i = 0; i < n; i++) {
    const f = fastSeries[i]?.value;
    const s = slowSeries[i]?.value;
    const macd = (Number.isFinite(f) && Number.isFinite(s)) ? (f - s) : null;
    macdAsBars[i] = {
      time: bars[i]?.time,
      close: Number.isFinite(macd) ? macd : NaN,  // NaN signals computeEMA to skip
    };
  }

  // Step 2: signal EMA over the macd line. computeEMA tolerates NaN closes
  // by leaving null in those positions, which is the warm-up behavior we want.
  // It begins emitting values once it has `signal` consecutive finite inputs.
  const signalSeries = computeEMA(macdAsBars, signal);

  // Step 3: compose output
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    const macd = Number.isFinite(macdAsBars[i].close) ? macdAsBars[i].close : null;
    const sig = signalSeries[i]?.value ?? null;
    const hist = (Number.isFinite(macd) && Number.isFinite(sig)) ? (macd - sig) : null;
    out[i] = {
      time: bars[i]?.time,
      macd,
      signal: sig,
      histogram: hist,
    };
  }
  return out;
};

/**
 * Read MACD values at the bar containing `ts` (open-time semantics).
 * Returns the full { macd, signal, histogram } point or null when not
 * yet warmed up at that bar.
 */
export const macdAtTime = (macdSeries, ts) => {
  if (!Array.isArray(macdSeries) || macdSeries.length === 0) return null;
  if (!Number.isFinite(ts)) return null;
  let chosen = null;
  for (const pt of macdSeries) {
    if (pt?.time == null) continue;
    if (pt.time <= ts) chosen = pt;
    else break;
  }
  if (!chosen || chosen.histogram == null) return null;
  return chosen;
};
